import {
	type PropsWithChildren,
	createContext,
	type Dispatch,
	type FunctionComponent,
	type SetStateAction,
	useState,
	useMemo,
	useCallback
} from 'react';
import { type PricedOptionGroupFieldsFragment, type PricedOptionSelectionFieldsFragment } from '../../__generated__/graphql-client-types';
import { type KeycodeTextInput, useKeycodeValidation } from '../../hooks/validator/keycode-validation.hooks';
import type { ProductSubItem } from '../../types/product.types';

export type ProductPricedOptionsContextState = {
	selectedOptions: (PricedOptionSelectionFieldsFragment | null)[];
	textInputOptions: PricedOptionSelectionFieldsFragment[];
	textInputs: Record<string, KeycodeTextInput>;
	handleSetTextInput: (option: PricedOptionSelectionFieldsFragment, value: string) => void;
	atcLoading: boolean;
	setAtcLoading: Dispatch<SetStateAction<boolean>>;
	configurationValid: boolean;
	handleSetOption: (option: PricedOptionSelectionFieldsFragment | null, index: number) => void;
	subItems: ProductSubItem[];
	setSubItems: Dispatch<SetStateAction<ProductSubItem[]>>;
	mainItemQuantity: number;
	setMainItemQuantity: Dispatch<SetStateAction<number>>;
};

export const pricedOptionsContextInitialState: ProductPricedOptionsContextState = {
	selectedOptions: [],
	textInputOptions: [],
	textInputs: {},
	handleSetTextInput: () => {},
	atcLoading: false,
	setAtcLoading: () => {},
	configurationValid: false,
	handleSetOption: () => {},
	subItems: [],
	setSubItems: () => {},
	mainItemQuantity: 1,
	setMainItemQuantity: () => {}
};

export const ProductPricedOptionsContext = createContext<ProductPricedOptionsContextState>({ ...pricedOptionsContextInitialState });

export const ProductPricedOptionsContextProvider: FunctionComponent<
	PropsWithChildren<{ productOptionsGroups: PricedOptionGroupFieldsFragment[]; variantId: number; quantityParam?: number }>
> = ({ children, productOptionsGroups, variantId, quantityParam }) => {
	const [selectedOptions, setSelectedOptions] = useState<(PricedOptionSelectionFieldsFragment | null)[]>(() =>
		productOptionsGroups.map((group) => group.pricedOptions.find((option) => option.isDefaultSelection) || null)
	);
	const [atcLoading, setAtcLoading] = useState(false);
	const [subItems, setSubItems] = useState<ProductSubItem[]>([]);
	const [mainItemQuantity, setMainItemQuantity] = useState<number>(Number(quantityParam || 1));
	// Keycode entry and validation is owned by the hook; this context only reads the verdicts
	const { textInputs, textInputOptions, handleSetTextInput } = useKeycodeValidation(variantId);

	// Uses the functional updater so batched calls compose instead of overwriting each other,
	// and so the callback identity stays stable across selection changes
	const handleSetOption = useCallback((option: PricedOptionSelectionFieldsFragment | null, index: number) => {
		setSelectedOptions((prev) => {
			const newSelectedOptions = [...prev];
			newSelectedOptions[index] = option;
			return newSelectedOptions;
		});
	}, []);

	// Cart API rejects the configuration unless every required group has a selection and every text input
	// is confirmed valid - a pending or rejected keycode must not be purchasable.
	const configurationValid = useMemo(() => {
		if (Object.values(textInputs).some((textInput) => textInput.status !== 'valid')) return false;

		return productOptionsGroups.every((group, index) => {
			if (!group.required) return true;
			if (selectedOptions[index]) return true;
			// TEXTBOX options never populate selectedOptions - they report through textInputs
			return group.pricedOptions.some((option) => option.type === 'TEXTBOX' && textInputs[option.id]?.status === 'valid');
		});
	}, [productOptionsGroups, selectedOptions, textInputs]);

	const productPricedOptionsContextValue = useMemo(
		() => ({
			selectedOptions,
			textInputOptions,
			textInputs,
			handleSetTextInput,
			atcLoading,
			setAtcLoading,
			configurationValid,
			handleSetOption,
			subItems,
			setSubItems,
			mainItemQuantity,
			setMainItemQuantity
		}),
		[
			selectedOptions,
			textInputOptions,
			textInputs,
			handleSetTextInput,
			atcLoading,
			configurationValid,
			handleSetOption,
			subItems,
			mainItemQuantity,
			setMainItemQuantity
		]
	);
	return <ProductPricedOptionsContext.Provider value={productPricedOptionsContextValue}>{children}</ProductPricedOptionsContext.Provider>;
};
